import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Close, ErrorOutline, Menu, Send } from '@mui/icons-material'
import { ReactComponent as AiLogo } from '../../../../../assets/icons/common/ai-logo.svg'
import { ReactComponent as AiLogoDark } from '../../../../../assets/icons/common/ai-logo-dark.svg'
import { Controller, useForm } from 'react-hook-form'
import { motion } from 'framer-motion'
import { useMediaQuery } from 'react-responsive'
import { IconButton, Textarea } from '../../../../../components/ui'
import cn from '../../../../../lib/utils/cn'
import { ChatsItem } from '../../../../../lib/api/types/document.interfaces'
import {
	useChatMessages,
	useCreateChat,
	useSendUserMessage,
} from '../../../../../hooks/api/chatHooks'
import LockerLoader from '../../../../../components/common/LockerLoader'
import Loader from '../../../../../components/common/Loader'
import { useQueryClient } from '@tanstack/react-query'
import { MessagesItem } from '../../../../../lib/api/types/chat.interfaces'
import { useAuth } from '../../../../../hooks'

interface ChatProps {
	isOpen: boolean
	onClose: () => void
	onOpenMenu: () => void
	onSelect: (chat: ChatsItem | null) => void
	onSave?: () => void
	isMenuOpen: boolean
	currentFileId?: string | null
	selectedChat: ExtendedChatsItem | null
}

type ExtendedChatsItem = ChatsItem & Partial<{ isNew: boolean }>

const Chat: FC<ChatProps> = ({
	isOpen,
	onClose,
	onOpenMenu,
	onSelect,
	onSave,
	isMenuOpen,
	currentFileId,
	selectedChat,
}) => {
	const { isAuthenticated, currentUser } = useAuth()
	const isMedium = useMediaQuery({ maxWidth: 1024 })
	const isLarge = useMediaQuery({ maxWidth: 1680 })

	const [chatError, setChatError] = useState<any>(null)
	const [pendingMessages, setPendingMessages] = useState<MessagesItem[]>([])
	const queryClient = useQueryClient()

	const listRef = useRef<HTMLDivElement>(null)

	const { control, watch, reset } = useForm({
		defaultValues: {
			message: '',
		},
	})

	const { message } = watch()

	const { data: chatData, isLoading } = useChatMessages(
		{
			id: selectedChat?.id || '',
		},
		{ enabled: Boolean(selectedChat?.id && isAuthenticated && !selectedChat?.isNew) }
	)

	const { mutateAsync: createNewChat, isPending: isChatCreating } =
		useCreateChat()

	const {
		mutateAsync: sendUserMessage,
		isPending: isMessageSending,
		currentSendingIds,
	} = useSendUserMessage()

	const handleClose = () => {
		if (chatData?.data?.id && chatData?.data?.isTemporary) {
			onSave?.()
			return
		}
		reset({ message: '' })
		setChatError(null)
		onClose()
	}

	const handleSendMessage = async ({
		message,
		senderId,
		chatId,
	}: {
		message: string
		senderId: string
		chatId?: string
	}) => {
		if (!currentFileId || (!selectedChat?.id && !chatId)) {
			setPendingMessages((prev) => [
				...prev,
				{
					id: `temp-${Date.now()}`,
					text: message,
					senderId,
					createdAt: new Date(),
					is_error: false,
				},
			])
			return
		}
		if (chatError) setChatError(null)
		reset({ message: '' })
		const send = async (id: string, text: string) =>
			await sendUserMessage(
				{ id, text, senderId },
				{
					onError: (error: any) => setChatError({ error, id }),
				}
			)
		await send(selectedChat?.id || chatId || '', message)
	}

	const onSentMessage = async () => {
		if (!message?.trim()?.length) return
		if (selectedChat?.id) {
			handleSendMessage({
				message,
				senderId: currentUser?.id,
			})
			return
		}
		await createNewChat(
			{
				fileId: currentFileId ?? '',
				title: message?.slice(20),
				isTemporary: true,
				invalidate: false,
			},
			{
				onSuccess: ({ data }) => {
					onSelect(data?.data)
					handleSendMessage({
						message,
						senderId: currentUser?.id,
						chatId: data?.data?.id,
					})
				},
			}
		)
	}

	const chatMessages = useMemo(
		() =>
			chatData?.data?.messages?.sort(
				(a, b) =>
					new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
			),
		[chatData, isMessageSending]
	)

	useEffect(() => {
		if (listRef.current) {
			listRef.current.scrollTo({
				top: listRef.current.scrollHeight,
				behavior: 'smooth',
			})
		}
	}, [chatMessages])

	useEffect(() => {
		if (selectedChat?.id && pendingMessages.length > 0) {
			setPendingMessages([])
		}
	}, [selectedChat?.id])

	if (!isOpen) return null

	return (
		<motion.div
			initial={{ x: '-100%' }}
			animate={{ x: isOpen ? 0 : '-100%' }}
			exit={{ x: '-100%' }}
			transition={{ duration: isMedium ? 0 : 0.1, ease: 'easeInOut' }}
			className={cn(
				'fixed inset-y-0 left-40 z-20 h-full flex flex-col justify-start items-start box-border border-r border-stroke-dark dark:border-stroke-dark-dark bg-white dark:bg-background-dark',
				isMedium ? 'w-screen inset-0' : isLarge ? 'w-[300px]' : 'w-[560px]',
				isMenuOpen ? 'left-40' : 'left-0'
			)}
		>
			<div className="h-20 w-full border-b border-stroke-dark dark:border-stroke-dark-dark box-border flex items-center justify-between px-2.5">
				{isMedium || !isMenuOpen ? (
					<IconButton
						variant="transparent"
						icon={<Menu className="dark:fill-text-main-dark" />}
						onClick={onOpenMenu}
						className="w-11 h-11 max-h-11"
					/>
				) : (
					<span></span>
				)}

				<IconButton
					variant="transparent"
					icon={<Close className="dark:fill-text-main-dark" />}
					onClick={handleClose}
					className="w-11 h-11 max-h-11"
				/>
			</div>
			<div
				ref={listRef}
				className="relative p-2.5 w-full box-border h-auto flex-1 flex flex-col gap-4 border-b border-stroke-dark dark:border-stroke-dark-dark overflow-y-auto"
			>
				<div className="flex flex-col gap-4 mt-auto justify-end">
					{selectedChat?.id
						? chatMessages?.map(({ id, text, senderId, is_error }) => {
							const isError = Boolean(is_error && senderId)
							return (
								<div key={id} className="flex items-end gap-1 mt-auto">
									<div
										className={cn(
											'p-2 text-sm text-text-main box-border',
											senderId
												? 'ml-auto w-fit max-w-[200px] min-h-9 bg-background-light-hover dark:bg-white px-3 rounded-lg rounded-br-none'
												: 'bg-accent-light border border-accent rounded-lg w-full box-border p-3',
											isError &&
											'border border-negative bg-negative/10 text-negative'
										)}
									>
										{!senderId && (
											<div className="flex items-center justify-start mb-2 gap-2">
												<AiLogo className="w-5 h-5" />
												<span className="text-text-main text-sm font-bold">
													LUMA
												</span>
											</div>
										)}
										<p className="text-wrap text-sm overflow-hidden">
											{text}
										</p>
										{isError && (
											<div className="flex items-center gap-1 mt-1">
												<ErrorOutline className="w-4 h-4 fill-negative" />
												<span className="text-xs">Error sending...</span>
												<button
													onClick={() => {
														handleSendMessage({
															message: text,
															senderId: currentUser?.id,
															chatId: selectedChat?.id || '',
														})
													}}
													className="ml-2 text-xs text-accent underline cursor-pointer"
												>
													Retry
												</button>
											</div>
										)}
									</div>
									{!!senderId && (
										<div className="w-6 h-6 bg-background-light-hover rounded-md rounded-bl-none overflow-hidden ">
											<img
												src={currentUser?.profile?.avatar}
												alt="user-avatar"
												className="w-full h-full"
											/>
										</div>
									)}
								</div>
							)
						})
						: pendingMessages.map(({ id, text, senderId, is_error }) => {
							const isError = Boolean(is_error && senderId)
							return (
								<div key={id} className="flex items-end gap-1 mt-auto">
									<div
										className={cn(
											'p-2 text-sm text-text-main box-border',
											senderId
												? 'ml-auto w-fit max-w-[200px] min-h-9 bg-background-light-hover dark:bg-white px-3 rounded-lg rounded-br-none'
												: 'bg-accent-light border border-accent rounded-lg w-full box-border p-3',
											isError &&
											'border border-negative bg-negative/10 text-negative'
										)}
									>
										{!senderId && (
											<div className="flex items-center justify-start mb-2 gap-2">
												<AiLogo className="w-5 h-5" />
												<span className="text-text-main text-sm font-bold">
													LUMA
												</span>
											</div>
										)}
										<p className="text-wrap text-sm overflow-hidden">
											{text}
										</p>
										{isError && (
											<div className="flex items-center gap-1 mt-1">
												<ErrorOutline className="w-4 h-4 fill-negative" />
												<span className="text-xs">Send failed</span>
												<button
													onClick={() => {
														handleSendMessage({
															message: text,
															senderId: currentUser?.id,
															chatId: selectedChat?.id || '',
														})
													}}
													className="ml-2 text-xs text-accent underline cursor-pointer"
												>
													Retry
												</button>
											</div>
										)}
									</div>
									{!!senderId && (
										<div className="w-6 h-6 bg-background-light-hover rounded-md rounded-bl-none overflow-hidden ">
											<img
												src={currentUser?.profile?.avatar}
												alt="user-avatar"
												className="w-full h-full"
											/>
										</div>
									)}
								</div>
							)
						})}
				</div>

				{isMessageSending &&
					!!selectedChat &&
					currentSendingIds?.includes(selectedChat?.id) && (
						<div className="p-2 bg-accent-light border border-accent rounded-lg w-fit min-w-8 h-fit box-border text-sm text-text-main">
							<div className="flex items-center justify-start mb-2  gap-2">
								<AiLogo className="w-5 h-5" />
								<span className="text-text-main text-sm font-bold">LUMA</span>
							</div>
							<div className="flex items-center gap-1.5">
								<span>Thinking</span>{' '}
								<Loader size={10} classNameWrapper="mt-0.5" />
							</div>
						</div>
					)}
				{chatError && chatError?.id === selectedChat?.id && (
					<div className="w-full bg-negative/15 border-negative border gap-2 rounded-lg p-2 box-border flex justify-start items-start">
						<ErrorOutline className="fill-negative" />
						<p className="text-sm text-wrap mt-0.5">
							{chatError?.error?.response?.status < 500
								? "Sorry, I don't understand that question. Can you please rephrase?"
								: 'Oops! need more time to catchup, please try sending your question later.'}
						</p>
					</div>
				)}
				{!chatMessages?.length && !(isMessageSending || isLoading) && (
					<p className="text-xs text-gray-400 mt-auto text-center italic">
						Ask question to start a conversation
					</p>
				)}
				<LockerLoader visible={isLoading} />
			</div>

			<div className="px-2.5 flex items-center h-16 gap-1.5 w-full box-border">
				<Controller
					name="message"
					control={control}
					render={({ field: { value, onChange } }) => (
						<Textarea
							placeholder="Ask a question"
							value={value}
							onChange={onChange}
							onEnterClick={onSentMessage}
							rows={1}
							wrapperClassName="w-auto h-11 flex-1"
						/>
					)}
				/>
				<IconButton
					icon={<Send />}
					onClick={onSentMessage}
					className="w-11 h-11 max-h-11"
					disabled={!message?.trim()}
					loading={isMessageSending || isChatCreating}
				/>
			</div>
		</motion.div>
	)
}

export default Chat
